import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Banknote,
  CheckCircle,
  Phone,
  Mail,
  ArrowRight,
  Shield,
  Clock,
  AlertCircle,
  Circle,
} from 'lucide-react';
import { extractIdFromSlug, createListingSlug } from '../lib/slugUtils';

// Basis-URL für kanonische URLs (Produktionsumgebung)
const CANONICAL_BASE = 'https://www.firmbay.de';

/**
 * GmbHDetail - Premium GmbH-Detailseite
 * 
 * TasteSkill Premium V3 Final Design
 * Route: /gmbh-kaufen/:slug (Format: [titel-slug]-[short-id])
 * 
 * Phase 2.1 Optimierungen:
 * - Kaufpreiskarte mit Teal-Button und Verfügbarkeitsstatus
 * - Alternative GmbH anfragen für reservierte Gesellschaften
 * - Aktualisierter rechtlicher Hinweis (Entwurf)
 * - Responsive Optimierung für 360px, 390px, 768px, 1440px
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Anfrage-Typen
const INQUIRY_TYPE = {
  KAUFANFRAGE: 'kaufanfrage',
  ALTERNATIVE: 'alternative_gmbh',
};

/**
 * Generiert SEO-optimierte Metadaten für eine GmbH-Detailseite
 * @param {object|null} listing - Das Listing-Objekt oder null
 * @param {string} canonicalPath - Der kanonische URL-Pfad
 * @returns {object} SEO-Metadaten
 */
const generateSEOData = (listing, canonicalPath) => {
  if (!listing) {
    return {
      title: 'GmbH nicht gefunden | Firmbay',
      description: 'Die angeforderte Gesellschaft wurde nicht gefunden oder ist nicht mehr verfügbar.',
      canonicalUrl: `${CANONICAL_BASE}/marktplatz`,
      noIndex: true,
    };
  }

  // Individueller Seitentitel basierend auf Angebotsdaten
  const title = `${listing.title} | GmbH kaufen | Firmbay`;

  // Individuelle Meta-Description (max. 160 Zeichen empfohlen)
  const descriptionParts = [];
  if (listing.industry) descriptionParts.push(listing.industry);
  if (listing.location) descriptionParts.push(`Standort ${listing.location}`);
  if (listing.stammkapital) descriptionParts.push(`${listing.stammkapital.toLocaleString('de-DE')} € Stammkapital`);
  if (listing.price) descriptionParts.push(`Kaufpreis ${listing.price.toLocaleString('de-DE')} €`);
  
  let description = `${listing.title} – ${descriptionParts.join(', ')}.`;
  if (listing.status === 'Verfügbar') {
    description += ' Jetzt Kaufanfrage stellen bei Firmbay.';
  } else if (listing.status === 'Reserviert') {
    description += ' Alternative GmbH anfragen bei Firmbay.';
  }
  
  // Kürzen auf max. 160 Zeichen
  if (description.length > 160) {
    description = description.substring(0, 157) + '...';
  }

  // Absolute kanonische URL
  const canonicalUrl = `${CANONICAL_BASE}${canonicalPath}`;

  return {
    title,
    description,
    canonicalUrl,
    noIndex: false,
  };
};

const GmbHDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canonicalPath, setCanonicalPath] = useState('');
  
  // Anfrage-Formular State
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryType, setInquiryType] = useState(INQUIRY_TYPE.KAUFANFRAGE);
  const [inquiryData, setInquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // SEO-Daten basierend auf aktuellem Zustand
  const seoData = generateSEOData(
    error ? null : listing,
    canonicalPath || `/gmbh-kaufen/${slug}`
  );

  useEffect(() => {
    const loadListing = async () => {
      setLoading(true);
      setError(null);
      
      const shortId = extractIdFromSlug(slug);
      
      if (!shortId) {
        setError('Ungültige URL. Bitte verwenden Sie einen gültigen Link.');
        setLoading(false);
        return;
      }
      
      try {
        let res;
        try {
          res = await axios.get(`${API}/listings/by-short-id/${shortId}`);
        } catch (shortIdErr) {
          if (slug.length === 36 && /^[a-f0-9-]{36}$/i.test(slug)) {
            res = await axios.get(`${API}/listings/${slug}`);
          } else {
            throw shortIdErr;
          }
        }
        
        const loadedListing = res.data;
        setListing(loadedListing);
        
        const canonicalSlug = createListingSlug(loadedListing.title, loadedListing.id);
        // Setze den kanonischen Pfad für SEO
        setCanonicalPath(`/gmbh-kaufen/${canonicalSlug}`);
        
        if (slug !== canonicalSlug) {
          navigate(`/gmbh-kaufen/${canonicalSlug}`, { replace: true });
        }
      } catch (err) {
        console.error('Error loading listing:', err);
        if (err.response?.status === 404) {
          setError('Diese Gesellschaft wurde nicht gefunden oder ist nicht mehr verfügbar.');
        } else {
          setError('Fehler beim Laden der Gesellschaftsdaten.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadListing();
    }
  }, [slug, navigate]);

  // Reset form when closing
  const resetForm = () => {
    setShowInquiryForm(false);
    setInquiryData({ name: '', email: '', phone: '', message: '' });
    setInquiryType(INQUIRY_TYPE.KAUFANFRAGE);
  };

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryData(prev => ({ ...prev, [name]: value }));
  };

  const openInquiryForm = (type) => {
    setInquiryType(type);
    setShowInquiryForm(true);
    setSubmitted(false);
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Serverseitige Validierung: Listing-Daten werden vom Backend geladen
      const isAlternative = inquiryType === INQUIRY_TYPE.ALTERNATIVE;
      
      // Baue die Nachricht mit allen relevanten Angebotsinformationen
      const listingInfo = [
        `Anfrageart: ${isAlternative ? 'Alternative GmbH' : 'Kaufanfrage'}`,
        `Ursprüngliches Angebot: ${listing?.title}`,
        `Angebots-ID: ${listing?.id?.slice(-8)}`,
        `Kaufpreis: ${listing?.price?.toLocaleString('de-DE')} €`,
        `Status: ${listing?.status}`,
        `URL: ${window.location.href}`,
        '',
        'Nachricht des Interessenten:',
        inquiryData.message || '(keine Nachricht)',
      ].join('\n');

      const payload = {
        name: inquiryData.name,
        email: inquiryData.email,
        phone: inquiryData.phone || '',
        company: '',
        subject: isAlternative ? 'alternative_gmbh' : 'kaufanfrage',
        message: listingInfo,
      };

      await axios.post(`${API}/contact`, payload);
      
      setSubmitted(true);
      toast({
        title: isAlternative 
          ? "Anfrage erfolgreich gesendet!" 
          : "Kaufanfrage erfolgreich gesendet!",
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      toast({
        variant: "destructive",
        title: "Fehler beim Senden",
        description: "Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>GmbH wird geladen... | Firmbay</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-[#F8FAFA] pt-24 pb-12 px-4">
          <div className="max-w-[1000px] mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="bg-white rounded-xl p-8">
                <div className="h-6 bg-gray-200 rounded w-2/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={seoData.canonicalUrl} />
        </Helmet>
        <div className="min-h-screen bg-[#F8FAFA] pt-24 pb-12 px-4">
          <div className="max-w-[600px] mx-auto text-center">
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-xl font-bold text-[#0B1930] mb-2">Nicht gefunden</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link to="/marktplatz">
                <Button className="bg-[#0B1930] hover:bg-[#162844] text-white">
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Zurück zum Marktplatz
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!listing) return null;

  const isAvailable = listing.status === 'Verfügbar';
  const isReserved = listing.status === 'Reserviert';

  return (
    <>
      {/* SEO Meta-Tags - Dynamisch basierend auf Listing-Daten */}
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        
        {/* Open Graph Tags für Social Media */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:site_name" content="Firmbay" />
        
        {/* Strukturierte Daten Hinweise */}
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="min-h-screen bg-[#F8FAFA]" data-testid="gmbh-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#009E9A]">Startseite</Link>
            <span className="text-gray-300">/</span>
            <Link to="/marktplatz" className="text-gray-500 hover:text-[#009E9A]">Marktplatz</Link>
            <span className="text-gray-300">/</span>
            <span className="text-[#0B1930] font-medium truncate max-w-[200px]">{listing.title}</span>
          </div>
        </div>
      </div>

      <div className="pt-6 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1280px] mx-auto">
          {/* Back Button */}
          <Link 
            to="/marktplatz" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#009E9A] mb-6 transition-colors"
            data-testid="back-to-marktplatz"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </Link>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    isAvailable 
                      ? 'bg-[#E9F7F6] text-[#009E9A]' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {listing.status}
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {listing.industry}
                  </span>
                </div>
                
                <h1 className="text-xl sm:text-2xl md:text-[28px] font-bold text-[#0B1930] mb-4 leading-tight" data-testid="listing-title">
                  {listing.title}
                </h1>

                {/* Key Metrics - Responsive Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-[#F8FAFA] rounded-lg">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">Standort</span>
                    </div>
                    <div className="font-semibold text-[#0B1930] text-sm sm:text-base truncate">{listing.location}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">Gründung</span>
                    </div>
                    <div className="font-semibold text-[#0B1930] text-sm sm:text-base">{listing.founded_year}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <Banknote className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">Stammkapital</span>
                    </div>
                    <div className="font-semibold text-[#0B1930] text-sm sm:text-base">{listing.stammkapital?.toLocaleString('de-DE')} €</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">Kaufpreis</span>
                    </div>
                    <div className="font-semibold text-[#009E9A] text-sm sm:text-base">{listing.price?.toLocaleString('de-DE')} €</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {listing.description && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                  <h2 className="font-bold text-[#0B1930] mb-3 text-base sm:text-lg">Beschreibung</h2>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                    {listing.description}
                  </p>
                </div>
              )}

              {/* Features */}
              {listing.features && listing.features.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                  <h2 className="font-bold text-[#0B1930] mb-4 text-base sm:text-lg">Merkmale & Ausstattung</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {listing.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#009E9A] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimer - ENTWURF (Rechtliche Prüfung ausstehend) */}
              <div className="bg-[#E9F7F6] border border-[#009E9A]/20 rounded-xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#009E9A] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[#0B1930] mb-1.5 text-sm">Hinweis zu den Gesellschaftsangaben</h3>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                      Die Informationen zu den angebotenen Gesellschaften beruhen auf Angaben der jeweiligen Verkäufer. 
                      FirmBay übernimmt keine Gewähr für die Richtigkeit, Vollständigkeit oder Aktualität dieser Angaben. 
                      Interessenten wird empfohlen, die rechtlichen, steuerlichen und wirtschaftlichen Verhältnisse der 
                      Gesellschaft vor dem Erwerb eigenständig oder durch entsprechend qualifizierte Berater prüfen zu lassen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Price & Inquiry */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                {/* Price Card - Optimiert für Phase 2.1 */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6" data-testid="price-card">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Kaufpreis</div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#0B1930] mb-3" data-testid="listing-price">
                    {listing.price?.toLocaleString('de-DE')} €
                  </div>
                  
                  {/* Status Badge */}
                  <div className={`flex items-center gap-2 mb-4 ${isAvailable ? 'text-[#009E9A]' : 'text-yellow-600'}`}>
                    <Circle className={`w-2.5 h-2.5 fill-current ${isAvailable ? 'text-green-500' : 'text-yellow-500'}`} />
                    <span className="text-sm font-medium">{listing.status}</span>
                  </div>
                  
                  {isAvailable ? (
                    /* Verfügbare GmbH - Kaufanfrage */
                    <>
                      {!showInquiryForm && !submitted && (
                        <>
                          <Button 
                            onClick={() => openInquiryForm(INQUIRY_TYPE.KAUFANFRAGE)}
                            className="w-full h-12 bg-[#009E9A] hover:bg-[#008785] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:ring-offset-2"
                            data-testid="inquiry-button"
                          >
                            Kaufanfrage stellen
                          </Button>
                          <p className="text-xs text-gray-500 text-center mt-3">
                            Persönliche Beratung und Begleitung bei der Übernahme.
                          </p>
                        </>
                      )}
                    </>
                  ) : isReserved ? (
                    /* Reservierte GmbH - Alternative anfragen */
                    <>
                      {!showInquiryForm && !submitted && (
                        <div className="space-y-4">
                          <div className="bg-yellow-50 rounded-lg p-3">
                            <p className="text-sm text-yellow-800">
                              Diese Gesellschaft ist derzeit reserviert.
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Wir unterstützen Sie gerne bei der Suche nach einer vergleichbaren GmbH.
                            </p>
                          </div>
                          <Button 
                            onClick={() => openInquiryForm(INQUIRY_TYPE.ALTERNATIVE)}
                            className="w-full h-12 bg-[#009E9A] hover:bg-[#008785] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:ring-offset-2"
                            data-testid="alternative-inquiry-button"
                          >
                            Alternative GmbH anfragen
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Anderer Status */
                    <div className="text-center py-4 px-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Status: {listing.status}
                      </p>
                    </div>
                  )}
                </div>

                {/* Inline Inquiry Form */}
                {showInquiryForm && !submitted && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6" data-testid="inquiry-form">
                    <h3 className="font-bold text-[#0B1930] mb-2 text-base">
                      {inquiryType === INQUIRY_TYPE.ALTERNATIVE 
                        ? 'Alternative GmbH anfragen' 
                        : 'Kaufanfrage senden'}
                    </h3>
                    {inquiryType === INQUIRY_TYPE.ALTERNATIVE && (
                      <p className="text-xs text-gray-600 mb-4">
                        Sie interessieren sich für eine Gesellschaft, die derzeit reserviert ist? 
                        Teilen Sie uns Ihre Anforderungen mit. Wir prüfen gerne passende Alternativen.
                      </p>
                    )}
                    
                    <form onSubmit={handleInquirySubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1">
                          Name <span className="text-[#009E9A]">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={inquiryData.name}
                          onChange={handleInquiryChange}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-transparent"
                          placeholder="Vorname und Nachname"
                          data-testid="inquiry-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1">
                          E-Mail <span className="text-[#009E9A]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={inquiryData.email}
                          onChange={handleInquiryChange}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-transparent"
                          placeholder="ihre@email.de"
                          data-testid="inquiry-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={inquiryData.phone}
                          onChange={handleInquiryChange}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-transparent"
                          placeholder="Optional"
                          data-testid="inquiry-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0B1930] mb-1">
                          Nachricht
                        </label>
                        <textarea
                          name="message"
                          rows={3}
                          value={inquiryData.message}
                          onChange={handleInquiryChange}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:border-transparent resize-none"
                          placeholder={inquiryType === INQUIRY_TYPE.ALTERNATIVE 
                            ? "Welche Art von GmbH suchen Sie? Gibt es bestimmte Anforderungen an Branche, Alter, Standort oder Kaufpreis?"
                            : "Ihre Fragen oder Anmerkungen..."
                          }
                          data-testid="inquiry-message"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full h-12 bg-[#009E9A] hover:bg-[#008785] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#009E9A] focus:ring-offset-2"
                        data-testid="inquiry-submit"
                      >
                        {submitting ? 'Wird gesendet...' : 'Anfrage senden'}
                      </Button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                      >
                        Abbrechen
                      </button>
                    </form>
                  </div>
                )}

                {/* Success Message */}
                {submitted && (
                  <div className="bg-[#E9F7F6] rounded-xl border border-[#009E9A]/30 p-5 sm:p-6 text-center" data-testid="inquiry-success">
                    <div className="w-12 h-12 bg-[#009E9A] rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-[#0B1930] mb-2">
                      {inquiryType === INQUIRY_TYPE.ALTERNATIVE 
                        ? 'Vielen Dank für Ihre Anfrage!' 
                        : 'Anfrage gesendet!'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {inquiryType === INQUIRY_TYPE.ALTERNATIVE 
                        ? 'Wir haben Ihre Anfrage nach einer alternativen GmbH erhalten und werden uns schnellstmöglich mit Ihnen in Verbindung setzen.'
                        : 'Vielen Dank für Ihr Interesse. Wir melden uns in Kürze bei Ihnen.'}
                    </p>
                  </div>
                )}

                {/* Contact Info - Nur wenn kein Formular offen */}
                {!showInquiryForm && !submitted && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
                    <h3 className="font-semibold text-[#0B1930] mb-3 text-sm">Fragen?</h3>
                    <div className="space-y-2">
                      <a 
                        href="tel:+491707967959" 
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#009E9A] transition-colors min-h-[44px]"
                      >
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        0170-7967959
                      </a>
                      <a 
                        href="mailto:info@firmbay.de" 
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#009E9A] transition-colors min-h-[44px]"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        info@firmbay.de
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default GmbHDetail;
